import type { ReactNode } from "react";
import { Badge, Group, Popover, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";

/**
 * Admin 列表页布局约定：
 * - 标题行：标题 + 可选风险 badge（Popover）+ 主操作；禁止顶部长 Alert 说明书
 * - 工具栏：搜索/筛选一行；状态用 xs dimmed 文案，不用整页 Alert
 * - 表单/Modal/错误/删除确认 仍可用 Alert
 */

export function AdminPageHeader(props: {
  readonly title: string;
  /** 一行副文案；长教程请用 Tooltip，勿写成长 Alert */
  readonly description?: string;
  readonly risk?: ReactNode;
  readonly actions?: ReactNode;
}) {
  const { title, description, risk, actions } = props;
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb="sm">
      <Stack gap={4} style={{ flex: "1 1 200px", minWidth: 0 }}>
        <Group gap="sm" wrap="wrap" align="center">
          <Text fw={600} size="lg">
            {title}
          </Text>
          {risk}
        </Group>
        {description !== undefined && description !== "" && (
          <Text size="xs" c="dimmed" maw={720}>
            {description}
          </Text>
        )}
      </Stack>
      {actions !== undefined && (
        <Group gap="xs" wrap="wrap">
          {actions}
        </Group>
      )}
    </Group>
  );
}

/** 标题旁风险入口：默认收起，点开再看详情 */
export function AdminRiskBadge(props: {
  readonly label: string;
  readonly color?: string;
  readonly children: ReactNode;
}) {
  const { label, color = "red", children } = props;
  return (
    <Popover width={360} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <UnstyledButton style={{ lineHeight: 1 }} aria-label={label}>
          <Badge color={color} size="sm" variant="filled" style={{ cursor: "pointer" }}>
            {label}
          </Badge>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">{children}</Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/** 工具栏内状态字，替代筛选结果 Alert */
export function AdminFilterStatus(props: {
  readonly children: ReactNode;
  readonly tone?: "dimmed" | "warn" | "ok";
}) {
  const { children, tone = "dimmed" } = props;
  const c = tone === "warn" ? "orange" : tone === "ok" ? "teal" : "dimmed";
  return (
    <Text size="xs" c={c} style={{ whiteSpace: "nowrap" }}>
      {children}
    </Text>
  );
}

/** 列头/按钮旁短提示 */
export function AdminHelpTip(props: { readonly label: string; readonly children?: ReactNode }) {
  const { label, children } = props;
  return (
    <Tooltip label={label} multiline maw={320} withArrow openDelay={200}>
      <UnstyledButton type="button" style={{ lineHeight: 1 }} aria-label="说明">
        {children ?? (
          <Badge size="xs" variant="outline" color="gray" style={{ cursor: "help" }}>
            ?
          </Badge>
        )}
      </UnstyledButton>
    </Tooltip>
  );
}

export function AdminFilterBar(props: { readonly children: ReactNode }) {
  return (
    <Group gap="sm" wrap="wrap" align="center" mb="sm">
      {props.children}
    </Group>
  );
}
